<template>
  <v-dialog :value="value" max-width="1100" @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span>{{ title }}</span>
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div v-if="points.length < 2" class="py-6 text-center grey--text">
          Sin datos suficientes para graficar.
        </div>
        <div v-else-if="value" style="height: 560px;">
          <line-chart :chart-data="chartData" :options="chartOptions" />
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { Line } from 'vue-chartjs';
import moment from 'moment';

const LineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    // The dialog's content is inserted right as its open transition starts,
    // so measuring the canvas synchronously here can catch it at 0 width.
    // Deferring a tick lets the dialog finish laying out first.
    this.$nextTick(() => {
      this.renderChart(this.chartData, this.options);
    });
  },
  watch: {
    chartData: {
      deep: true,
      handler(newData) {
        if (this.$data._chart) {
          this.$data._chart.destroy();
        }
        this.renderChart(newData, this.options);
      },
    },
  },
};

export default {
  name: 'RefundTrendDialog',
  components: { LineChart },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    // Array of { period: 'YYYY-MM-DD', value: number }
    points: {
      type: Array,
      default: () => [],
    },
    color: {
      type: String,
      default: '#104b94',
    },
    granularity: {
      type: String,
      default: 'month', // 'day' | 'week' | 'month'
    },
    isPercentage: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    labels() {
      return this.points.map((p) => this.formatLabel(p.period));
    },
    chartData() {
      return {
        labels: this.labels,
        datasets: [
          {
            label: this.title,
            data: this.points.map((p) => p.value),
            borderColor: this.color,
            backgroundColor: this.hexToRgba(this.color, 0.08),
            pointBackgroundColor: this.color,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            fill: true,
            tension: 0.25,
          },
        ],
      };
    },
    chartOptions() {
      const isPercentage = this.isPercentage;
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (item) => `${item.yLabel}${isPercentage ? '%' : ''}`,
          },
        },
        scales: {
          xAxes: [{ gridLines: { display: false } }],
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                callback: (v) => `${v}${isPercentage ? '%' : ''}`,
              },
            },
          ],
        },
      };
    },
  },

  methods: {
    formatLabel(period) {
      if (this.granularity === 'day') return moment(period).format('DD MMM');
      if (this.granularity === 'week') return `Sem ${moment(period).format('DD MMM')}`;
      return moment(period).format('MMM YYYY');
    },
    hexToRgba(hex, alpha) {
      const clean = hex.replace('#', '');
      const bigint = Number.parseInt(clean, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>
